import { getUnixTime } from "date-fns";
import { uniqueId } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { AiOutlinePlusCircle } from "react-icons/ai";
import { BsImage, BsImageAlt } from "react-icons/bs";
import { FiTrash2, FiExternalLink } from "react-icons/fi";
import { IoIosDocument } from "react-icons/io";
import { Translate } from "react-localize-redux";
import { Link, useParams } from "react-router-dom";
import { auth } from "../../../App/routes";
import Button from "../../../utils/components/Button";
import Checkbox from "../../../utils/components/Checkbox";
import Input from "../../../utils/components/Input";
import Modal from "../../../utils/components/Modal";
import SelectBox from "../../../utils/components/SelectBox";
import timeStampToDateFormat from "../../../utils/helpers/timestampToDateFormat";
import { useUser } from "../../../utils/hooks/useUser";
import { useProject } from "../../../utils/hooks/useProject";
import API from "../../../utils/API";

// Status'u düzgün formatla
const getStatusName = (form) => {
  if (form?.status) {
    if (typeof form.status === 'object' && form.status.name) {
      return form.status.name;
    }
    return String(form.status);
  }
  const selectedStatus = form?.status_set?.find(
    (status) => status.selected === true
  );
  return selectedStatus ? String(selectedStatus.name || '') : '-';
};

// Location'ı düzgün formatla
const getLocationPath = (location) => {
  
  if (!location) return '-';
  if (typeof location === 'string') return location;
  if (Array.isArray(location)) {
    return location.map(loc => loc.name || loc).join(' > ');
  }
  return '-';
};

const ACTION_TYPES = [
  {
    id: 0,
    name: "original_forms",
    icon: <AiOutlinePlusCircle />,
  },
  {
    id: 1,
    name: "image",
    icon: <BsImage />,
  },
  {
    id: 2,
    name: "note",
    icon: <IoIosDocument />,
  },
];

const FormFieldActions = ({
  data = {},
  location,
  formTemplates,
  onChange,
  handleKeepOldForm,
  isEditing,
}) => {
  const [selectedItem, setSelectedItem] = useState({});
  const [file, setFile] = useState(null);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showActionChoiceModal, setShowActionChoiceModal] = useState(false);
  const [showSelectExistingModal, setShowSelectExistingModal] = useState(false);
  const [existingFormsList, setExistingFormsList] = useState([]);
  const [selectedExistingForms, setSelectedExistingForms] = useState([]);
  const [existingFormsLoading, setExistingFormsLoading] = useState(false);
  const [existingFormsPage, setExistingFormsPage] = useState(1);
  const [existingFormsTotalRows, setExistingFormsTotalRows] = useState(0);
  const [existingFormsSearch, setExistingFormsSearch] = useState("");
  const existingFormsSearchTimeoutRef = useRef(null);
  const [existingStatusFilter, setExistingStatusFilter] = useState([]);
  const [existingCreatedByFilter, setExistingCreatedByFilter] = useState([]);
  const [existingFormsFilterOptions, setExistingFormsFilterOptions] = useState({
    form_status: [],
    created_by: [],
  });

  const EXISTING_FORMS_PAGE_SIZE = 10;

  // Unlink için seçili formlar
  const [selectedForUnlink, setSelectedForUnlink] = useState([]);

  const toggleUnlinkSelection = (formId) => {
    setSelectedForUnlink((prev) =>
      prev.includes(formId) ? prev.filter((id) => id !== formId) : [...prev, formId]
    );
  };

  const handleSelectAllForUnlink = () => {
    if (selectedForUnlink.length === enrichedForms.length) {
      setSelectedForUnlink([]);
    } else {
      setSelectedForUnlink(enrichedForms.map((f) => f.id));
    }
  };

  const handleUnlinkSelected = () => {
    if (!onChange || !selectedForUnlink.length) return;
    onChange(
      (data?.original_forms || []).filter((f) => !selectedForUnlink.includes(f?.id)),
      "original_forms"
    );
    setSelectedForUnlink([]);
  };

  // Zenginleştirilmiş linked formları tut
  const [enrichedForms, setEnrichedForms] = useState([]);
  const [isLoadingForms, setIsLoadingForms] = useState(false);

  const user = useUser();
  const { project } = useProject();
  const { id: currentFormId } = useParams();

  // Linked formların detaylarını fetch et
  useEffect(() => {
    let isMounted = true;

    const fetchLinkedFormsDetails = async () => {
      // Eğer original_forms yoksa veya boşsa, skip et
      if (!data?.original_forms?.length) {
        if (isMounted) setEnrichedForms([]);
        return;
      }

      // Sadece ID'si olan formları filtrele
      const validForms = data.original_forms.filter(
        (form) => form?.id && typeof form === 'object' && !Array.isArray(form)
      );

      if (!validForms.length) {
        if (isMounted) setEnrichedForms([]);
        return;
      }

      if (isMounted) setIsLoadingForms(true);

      try {
        const formIds = validForms.map((form) => form.id);
        const { data: res } = await API.postData(
          `/forms/bulk?project_id=${project.id}`,
          { ids: formIds }
        );
        if (isMounted) setEnrichedForms(res.data);
      } catch (error) {
        if (isMounted) setEnrichedForms(validForms);
      } finally {
        if (isMounted) setIsLoadingForms(false);
      }
    };

    fetchLinkedFormsDetails();

    return () => {
      isMounted = false;
    };
  }, [data?.original_forms]);

  useEffect(() => {
    if (selectedItem?.id === 0 && !data?.original_forms?.length && isEditing) {
      setShowActionChoiceModal(true);
    }
  }, [data?.original_forms?.length, isEditing, selectedItem]);

  const handleSetFiles = (acceptedFiles) => {
    if (onChange)
      acceptedFiles.forEach((acceptedFile) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const newFileObj = {
            url: URL.createObjectURL(acceptedFile),
            base64: e.target.result,
            date: getUnixTime(new Date()),
            author: user?.name || "",
            zone:
              typeof location?.path === "string"
                ? location.path
                : location.map((item) => item.name).join(" > "),
            id: uniqueId("field-attachment-photo-"),
          };
          onChange(newFileObj, "image");

          setFile(newFileObj);
        };
        reader.readAsDataURL(acceptedFile);
      });
  };

  useEffect(() => {
    if (data?.image && !file) {
      setFile({
        url: data.image.url,
        base64: data.image.image,
        date: data.image.date,
        author: data.image.author,
        zone: data.image.zone,
        id: data.image.id,
      });
    }

    if (file && !data?.image && !data?.image_obj) {
      setFile(null);
    }
  }, [data?.image, data?.image_obj, file]);

  useEffect(() => {
    if (data?.image_obj) {
      setFile({
        url: data.image_obj.file_path,
        date: data.image_obj.created_at,
        author: data.image_obj.created_name,
        zone:
          typeof location?.path === "string"
            ? location.path
            : location.map((item) => item.name).join(" > "),
      });
    }
  }, [data?.image_obj, location]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/png": [".png", ".jpg", ".jpeg"],
    },
    multiple: false,
    onDrop: handleSetFiles,
    disabled: !Boolean(onChange),
  });

  const handleDeletePhoto = () => {
    if (onChange) {
      onChange("", "image");
      setFile(null);
    }
  };

  const fetchExistingFormsFilterOptions = async () => {
    try {
      const { data: res } = await API.postData("/forms/filter_table", {
        project_id: project.id,
      });
      setExistingFormsFilterOptions({
        form_status: res.data.form_status?.map((s) => ({ value: s, label: s })) || [],
        created_by: res.data.created_by?.map((c) => ({ value: c.id ?? c.value, label: c.name ?? c.label })) || [],
      });
    } catch (err) {
    }
  };

  const fetchExistingForms = async (page = 1, search = "", statuses = existingStatusFilter, createdBy = existingCreatedByFilter) => {
    setExistingFormsLoading(true);
    try {
      let url = `forms/filters?page=${page}&size=${EXISTING_FORMS_PAGE_SIZE}&es=true`;
      if (search) url += `&search=${search}`;
      const { data: res } = await API.postData(url, {
        project_id: project.id,
        statuses: statuses?.map((s) => s.value) || [],
        created_bys: createdBy?.map((c) => c.value) || [],
      });
      setExistingFormsList(res.data);
      setExistingFormsTotalRows(res.pagination.total_count);
      setExistingFormsPage(page);
    } catch (err) {
    } finally {
      setExistingFormsLoading(false);
    }
  };

  const handleExistingFormsSearch = (search) => {
    setExistingFormsSearch(search);
    if (existingFormsSearchTimeoutRef.current)
      clearTimeout(existingFormsSearchTimeoutRef.current);
    existingFormsSearchTimeoutRef.current = setTimeout(() => {
      fetchExistingForms(1, search, existingStatusFilter, existingCreatedByFilter);
    }, 500);
  };

  const toggleExistingFormSelection = (form) => {
    setSelectedExistingForms((prev) =>
      prev.some((f) => f.id === form.id)
        ? prev.filter((f) => f.id !== form.id)
        : [...prev, form]
    );
  };

  const handleAddExistingForms = () => {
    if (!selectedExistingForms.length || !onChange) return;
    const newOriginalForms = [
      ...(data?.original_forms || []),
      ...selectedExistingForms.map((form) => ({ id: form.id })),
    ];
    onChange(newOriginalForms, "original_forms");
    setShowSelectExistingModal(false);
    setSelectedExistingForms([]);
    setExistingFormsSearch("");
    setExistingStatusFilter([]);
    setExistingCreatedByFilter([]);
  };

  const handleCloseSelectExistingModal = () => {
    setShowSelectExistingModal(false);
    setSelectedExistingForms([]);
    setExistingFormsSearch("");
    setExistingStatusFilter([]);
    setExistingCreatedByFilter([]);
  };

  const searchParams = new URLSearchParams(window.location.search);

  if (searchParams.get("return")) {
    return <></>;
  }

  return (
    <div className="form-field-actions d-flex flex-column align-items-center w-100">
      <div className="action-items d-flex justify-content-center align-items-center mt-2">
        {ACTION_TYPES.map((item) => (
          <div
            key={item.id}
            className="action-item mx-2"
            data-name={item.name}
            onClick={() => {
              if (
                item.id === 0 &&
                !data?.original_forms?.length &&
                !isEditing
              ) {
                return;
              }

              setSelectedItem((prevSelectedItem) =>
                item.id === prevSelectedItem.id ? {} : item,
              );
            }}
            data-is-selected={
              data && data[item.name]
                ? selectedItem?.id === item.id ||
                (typeof data[item.name] === "string" &&
                  Boolean(data[item.name])) ||
                (Array.isArray(data[item.name]) &&
                  data[item.name]?.filter((d) => d?.id)?.length > 0) ||
                (typeof data[item.name] === "object" &&
                  Object.values(data[item.name])?.filter((d) => d?.id)
                    ?.length > 0) ||
                (item.name === "image" && Boolean(data?.image?.base64))
                : item.name === "image" && data?.image_obj?.file_path
                  ? true
                  : false
            }
            role="button"
            data-is-disabled={
              (item.id === 0 && !data?.original_forms?.length && !isEditing) ||
              (item.id === 1 && !data?.image_obj?.file_path && !isEditing) ||
              (item.id === 2 && !data?.note && !isEditing)
            }
          >
            {item.icon}
          </div>
        ))}
      </div>
      <div className="action-inner-area w-100">
        {selectedItem?.id === ACTION_TYPES[0].id && (
          <>
            {data?.original_forms?.length ? (
              <div className="linked-forms action-area position-relative">
                {/* Loading state */}
                {isLoadingForms ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-orange" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-2"><Translate id="forms.loading_linked_forms" /></p>
                  </div>
                ) : (
                  <>
                  {isEditing && (
                    <div className="d-flex align-items-center justify-content-between px-2 py-2 mb-2 rounded bg-light border">
                      <Checkbox
                        checked={enrichedForms.length > 0 && selectedForUnlink.length === enrichedForms.length}
                        onChange={handleSelectAllForUnlink}
                        label={<span className="fw-semibold small"><Translate id="forms.select_all" /></span>}
                      />
                      {selectedForUnlink.length > 0 && (
                        <small className="text-muted">
                          {selectedForUnlink.length} / {enrichedForms.length} <Translate id="forms.selected" />
                        </small>
                      )}
                    </div>
                  )}
                  <div className="linked-forms-container">
                    {enrichedForms?.map((form) => (
                      <div
                        key={form?.id}
                        className={`linked-form-card mb-3 p-3 border rounded shadow-sm hover-shadow${isEditing && selectedForUnlink.includes(form.id) ? " border-danger border-2" : ""}`}
                      >
                        {/* Form Header: checkbox + id/name link */}
                        <div className="d-flex align-items-center gap-2 mb-2">
                          {isEditing && (
                            <Checkbox
                              checked={selectedForUnlink.includes(form.id)}
                              onChange={() => toggleUnlinkSelection(form.id)}
                            />
                          )}
                          <Link
                            to={auth.formDetails.link.replace(':id', form.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-decoration-none d-flex align-items-center gap-2 flex-grow-1"
                          >
                            <span className="badge bg-orange">{String(form?.id || '')}</span>
                            <h6 className="mb-0 fw-bold">{form?.name || '-'}</h6>
                          </Link>
                        </div>

                        {/* Form Details Grid */}
                        <div className="row g-2 text-muted small">
                          <div className="col-6">
                            <div className="d-flex flex-column">
                              <span className="text-dark fw-semibold"><Translate id="forms.headers.status" />:</span>
                              <span>{getStatusName(form)}</span>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="d-flex flex-column">
                              <span className="text-dark fw-semibold"><Translate id="forms.event_list.headers.form_version" />:</span>
                              <span>{form?.version || '-'}</span>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="d-flex flex-column">
                              <span className="text-dark fw-semibold"><Translate id="forms.headers.created_at" />:</span>
                              <span>{timeStampToDateFormat(form?.created_at)}</span>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="d-flex flex-column">
                              <span className="text-dark fw-semibold"><Translate id="forms.headers.location" />:</span>
                              <span className="text-truncate" title={getLocationPath(form?.location_path)}>
                                {getLocationPath(form?.location_path)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="mt-3 d-flex gap-2">
                          <Link
                            to={auth.formDetails.link.replace(':id', form.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-orange text-white px-2 py-1 d-flex align-items-center"
                          >
                            <FiExternalLink className="me-2" />
                            <Translate id="forms.view-details" />
                          </Link>
                          {isEditing && (
                            <Button
                              className="btn-secondary px-2 py-1 d-flex align-items-center"
                              onClick={() => {
                                if (!onChange) return;
                                onChange(
                                  (data?.original_forms || []).filter((item) => item.id !== form.id),
                                  "original_forms"
                                );
                                setSelectedForUnlink((prev) => prev.filter((id) => id !== form.id));
                              }}
                            >
                              <FiTrash2 className="me-2" />
                              <Translate id="unlink" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  </>
                )}
                {isEditing && (
                  <div className="d-flex align-items-center justify-content-end gap-2 mt-3">
                    {selectedForUnlink.length > 0 && (
                      <Button
                        className="btn-m btn-secondary d-flex align-items-center"
                        onClick={handleUnlinkSelected}
                      >
                        <FiTrash2 className="me-1" />
                        <Translate id="forms.unlink_selected" />
                        <span className="ms-1 badge bg-white text-secondary">{selectedForUnlink.length}</span>
                      </Button>
                    )}
                    <Button
                      className="btn-m btn-orange text-white d-flex align-items-center"
                      onClick={() => setShowActionChoiceModal(true)}
                    >
                      <AiOutlinePlusCircle className="fs-4 me-1" />
                      <Translate id="forms.new-link" />
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="linked-forms action-area position-relative">
                <div className="no-linked-forms text-center py-3">
                  <p className="text-muted"><Translate id="forms.no_linked_forms" /></p>
                  {isEditing && (
                    <Button
                      className="btn-sm btn-orange text-white text-uppercase px-4 py-1"
                      onClick={() => setShowActionChoiceModal(true)}
                    >
                      <AiOutlinePlusCircle className="fs-4 me-1" />
                      <Translate id="forms.new-link" />
                    </Button>
                  )}
                </div>
              </div>
            )}

            <Translate>
              {({ translate }) => (
                <Modal
                  title={translate("forms.add_linked_form_title")}
                  modalSize="md"
                  show={showActionChoiceModal && Boolean(onChange)}
                  setShow={() => {
                    setShowActionChoiceModal(false);
                    if (
                      !data?.original_forms?.filter((form) => form?.id)?.length
                    )
                      setSelectedItem({});
                  }}
                >
                  <div className="d-flex flex-column gap-2 p-1">
                    <Button
                      className="btn-orange text-white w-100 py-2"
                      onClick={() => {
                        setShowActionChoiceModal(false);
                        setShowTemplatesModal(true);
                      }}
                    >
                      <AiOutlinePlusCircle className="fs-5 me-2" />
                      <Translate id="forms.create_new_form" />
                    </Button>
                    <Button
                      className="btn-secondary w-100 py-2"
                      onClick={() => {
                        setShowActionChoiceModal(false);
                        setShowSelectExistingModal(true);
                        fetchExistingForms(1, "", [], []);
                        fetchExistingFormsFilterOptions();
                      }}
                    >
                      <Translate id="forms.select_from_existing" />
                    </Button>
                  </div>
                </Modal>
              )}
            </Translate>

            <Translate>
              {({ translate }) => (
                <Modal
                  title={translate("forms.select_from_existing")}
                  modalSize="xl"
                  show={showSelectExistingModal && Boolean(onChange)}
                  setShow={handleCloseSelectExistingModal}
                >
                  {/* Filtreler */}
                  <div className="row g-2 mb-3">
                    <div className="col-12 col-md-4">
                      <Input
                        type="text"
                        placeholder={translate("forms.headers.form_name")}
                        value={existingFormsSearch}
                        onChange={(e) => handleExistingFormsSearch(e.target.value)}
                      />
                    </div>
                    <div className="col-12 col-md-4">
                      <SelectBox
                        options={existingFormsFilterOptions.form_status}
                        placeholder={translate("forms.place-holders.select-status")}
                        value={existingStatusFilter}
                        onChange={(val) => {
                          const newVal = val || [];
                          setExistingStatusFilter(newVal);
                          fetchExistingForms(1, existingFormsSearch, newVal, existingCreatedByFilter);
                        }}
                        isMulti
                        isClearable
                      />
                    </div>
                    <div className="col-12 col-md-4">
                      <SelectBox
                        options={existingFormsFilterOptions.created_by}
                        placeholder={translate("forms.place-holders.select-created-by-companies")}
                        value={existingCreatedByFilter}
                        onChange={(val) => {
                          const newVal = val || [];
                          setExistingCreatedByFilter(newVal);
                          fetchExistingForms(1, existingFormsSearch, existingStatusFilter, newVal);
                        }}
                        isMulti
                        isClearable
                      />
                    </div>
                  </div>

                  {/* Liste */}
                  {existingFormsLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-orange" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: "40px" }}></th>
                              <th>#</th>
                              <th><Translate id="forms.headers.form_name" /></th>
                              <th><Translate id="forms.uppercase_translates.status" /></th>
                              <th><Translate id="forms.uppercase_translates.created_by" /></th>
                              <th><Translate id="forms.uppercase_translates.location" /></th>
                              <th><Translate id="forms.uppercase_translates.modified_on" /></th>
                            </tr>
                          </thead>
                          <tbody>
                            {existingFormsList.filter((form) => form.id !== Number(currentFormId)).map((form) => {
                              const alreadyLinked =
                                data?.original_forms?.some((f) => f?.id === form.id);
                              const isSelected = selectedExistingForms.some(
                                (f) => f.id === form.id
                              );
                              return (
                                <tr
                                  key={form.id}
                                  className={isSelected ? "table-warning" : ""}
                                  style={{
                                    opacity: alreadyLinked ? 0.45 : 1,
                                    cursor: alreadyLinked ? "default" : "pointer",
                                  }}
                                  onClick={() =>
                                    !alreadyLinked && toggleExistingFormSelection(form)
                                  }
                                >
                                  <td>
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={isSelected}
                                      disabled={alreadyLinked}
                                      onChange={() => {}}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!alreadyLinked) toggleExistingFormSelection(form);
                                      }}
                                    />
                                  </td>
                                  <td className="text-muted small">{form.id}</td>
                                  <td>
                                    <span className="fw-semibold">{form.no}</span>
                                    <br />
                                    <small className="text-muted">{form.name}</small>
                                  </td>
                                  <td>{form.status_set_detail_name}</td>
                                  <td>{form.created_name}</td>
                                  <td>
                                    <small>{form.location_path}</small>
                                  </td>
                                  <td>
                                    <small>{timeStampToDateFormat(form.modified_at, "dd/MM/yyyy HH:mm")}</small>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Sayfalama */}
                      <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                        <small className="text-muted">
                          {existingFormsTotalRows} {translate("forms.total_results")}
                        </small>
                        <div className="d-flex gap-2 align-items-center">
                          <Button
                            className="btn-sm btn-outline-secondary"
                            disabled={existingFormsPage === 1 || existingFormsLoading}
                            onClick={() =>
                              fetchExistingForms(existingFormsPage - 1, existingFormsSearch)
                            }
                          >
                            &lsaquo;
                          </Button>
                          <small>
                            {existingFormsPage} / {Math.ceil(existingFormsTotalRows / EXISTING_FORMS_PAGE_SIZE) || 1}
                          </small>
                          <Button
                            className="btn-sm btn-outline-secondary"
                            disabled={
                              existingFormsPage >= Math.ceil(existingFormsTotalRows / EXISTING_FORMS_PAGE_SIZE) ||
                              existingFormsLoading
                            }
                            onClick={() =>
                              fetchExistingForms(existingFormsPage + 1, existingFormsSearch)
                            }
                          >
                            &rsaquo;
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Footer */}
                  <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
                    <Button
                      className="btn-secondary"
                      onClick={handleCloseSelectExistingModal}
                    >
                      <Translate id="cancel" />
                    </Button>
                    <Button
                      className="btn-orange text-white"
                      onClick={handleAddExistingForms}
                      disabled={!selectedExistingForms.length}
                    >
                      <Translate id="forms.add_selected" />
                      {selectedExistingForms.length > 0 && (
                        <span className="ms-1 badge bg-white text-orange">
                          {selectedExistingForms.length}
                        </span>
                      )}
                    </Button>
                  </div>
                </Modal>
              )}
            </Translate>

            <Translate>
              {({ translate }) => (
                <Modal
                  title={translate("form_templates")}
                  modalSize="lg"
                  show={showTemplatesModal && Boolean(onChange)}
                  setShow={() => {
                    setShowTemplatesModal(false);
                    if (
                      !data?.original_forms?.filter((form) => form?.id)?.length
                    )
                      setSelectedItem({});
                  }}
                >
                  <div className="form-templates-holder w-100 overflow-auto d-flex flex-column align-items-center">
                    {formTemplates?.map((item, index) => (
                      <Link
                        to={
                          auth.newForm.link.replace(":template", item.id) +
                          `?return=${window.location.pathname}`
                        }
                        className="cursor--pointer w-100 border-top border-1 ps-2 py-3 hover-green"
                        key={index}
                        onClick={(e) => {
                          if (!onChange) {
                            e.preventDefault();
                            return;
                          }

                          localStorage.setItem(
                            "new_form_location_sets",
                            JSON.stringify(location),
                          );

                          const newOriginalForms = data?.original_forms
                            ? [
                              ...data?.original_forms,
                              "new_form_id_placeholder",
                            ]
                            : ["new_form_id_placeholder"];

                          onChange(newOriginalForms, "original_forms");

                          handleKeepOldForm();
                        }}
                      >
                        <p className="mb-0">{item.name}</p>
                      </Link>
                    ))}
                  </div>
                </Modal>
              )}
            </Translate>
          </>
        )}
        {selectedItem?.id === ACTION_TYPES[1].id && (
          <div className="photo-area action-area position-relative">
            {file ? (
              <div className="uploaded-photo-area d-flex justify-content-start h-100">
                <img
                  src={file.url}
                  alt="attachment"
                  style={{ maxHeight: "136px" }}
                  className="flex-shrink-0 cursor--pointer"
                  onClick={() => setOpenImageModal(true)}
                />
                <Modal
                  modalSize="m"
                  show={openImageModal}
                  setShow={() => setOpenImageModal(false)}
                >
                  <div className="d-flex flex-column justify-content-center align-items-end">
                    <div className="d-flex justify-content-center align-items-center w-100">
                      <img
                        src={file.url}
                        alt="attachment"
                        className="mw-100 mh-100"
                      />
                    </div>
                    <div className="image-download-container mt-5 me-2 w-100 text-center">
                      <a
                        href={file.url}
                        target="_self"
                        rel="noopener noreferrer"
                        className="text-orange"
                      >
                        <Translate id="forms.click-for-download" />
                      </a>
                    </div>
                  </div>
                </Modal>
                <div className="info-area d-flex flex-column align-items-center w-100 ms-4 pt-2">
                  <p className="location text-secondary w-100 text-start">
                    {file.zone}
                  </p>
                  <p className="author text-secondary w-100 text-start">
                    {file.author} - {timeStampToDateFormat(file.date)}
                  </p>
                  {isEditing && (
                    <Button
                      className="btn-sm btn-secondary text-uppercase px-4 py-2 mt-4"
                      onClick={handleDeletePhoto}
                    >
                      <FiTrash2 className="fs-4"/> <Translate id="delete"/>
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              isEditing && (
                <div
                  {...getRootProps({
                    className:
                      "dropzone w-100 d-flex flex-column align-items-center",
                  })}
                  role="button"
                >
                  <input {...getInputProps()} />
                  <div className="icon border--dashed border-secondary border-3">
                    <div className="inner rounded-circle border border-1">
                      <BsImageAlt className="fs-1"/>
                    </div>
                  </div>
                  <p className="text text-center text-uppercase text-gray fw-light m-0">
                    <Translate id="forms.browse-files-to-upload-image"/>
                  </p>
                </div>
              )
            )}
          </div>
        )}
        {selectedItem?.id === ACTION_TYPES[2].id && (
          <div className="note-area action-area position-relative">
            <Translate>
              {({ translate }) => (
                <textarea
                  name="note"
                  placeholder={translate(
                    "forms.field_attachment_note_placeholder",
                  )}
                  className="w-100"
                  value={data?.note}
                  onChange={(e) => onChange(e.target.value, "note")}
                  disabled={!Boolean(onChange) || !isEditing}
                />
              )}
            </Translate>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormFieldActions;
